import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Check, Download } from 'lucide-react';
import { toast } from 'sonner';
import GiftModal from './GiftModal';

const DECK_CARDS = ['/cards/card_07_front.webp', '/cards/card_01_front.webp', '/cards/card_22_front.webp'];

const API = process.env.REACT_APP_BACKEND_URL;

const TSHIRT_IMG = "https://customer-assets.emergentagent.com/job_3344cd9f-1cb7-499b-8f13-a3a84e1239be/artifacts/czmbntpu_The%20Alchemy%20Village%20t-shirt%20.png";
const JOURNAL_IMG = "https://customer-assets.emergentagent.com/job_3344cd9f-1cb7-499b-8f13-a3a84e1239be/artifacts/npw3objr_The%20Alchemy%20Village%20Journal%20.png";
const MUG_IMG = "https://customer-assets.emergentagent.com/job_3344cd9f-1cb7-499b-8f13-a3a84e1239be/artifacts/gq8o5g5m_Alchemy%20Village%20mug%20.png";
const HOODIE_IMG = "https://customer-assets.emergentagent.com/job_3344cd9f-1cb7-499b-8f13-a3a84e1239be/artifacts/479go40j_The%20alchemy%20village%20hoodie%20.png";

/* Workbook front + back covers — Cabrini's 2026 release. */
const EBOOK_COVER = "https://customer-assets.emergentagent.com/job_e81db766-430a-4b91-9c64-aad9677ac04c/artifacts/m8yxfebu_The%20main%20ingredient%20Book%20Cover%20.png";
const EBOOK_BACK_COVER = "https://customer-assets.emergentagent.com/job_e81db766-430a-4b91-9c64-aad9677ac04c/artifacts/q77vmdoh_The%20back%20of%20The%20Main%20Ingredient.png";

const PREVIEW_PRODUCTS = [
  { name: 'The Alchemy Village Tee',      image: TSHIRT_IMG },
  { name: 'The Alchemy Village Hoodie',   image: HOODIE_IMG  },
  { name: 'The Alchemy Village Journal',  image: JOURNAL_IMG },
  { name: 'The Alchemy Village Mug',      image: MUG_IMG     },
];

export default function MerchPage() {
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState('idle');
  const [buying, setBuying] = useState(false);
  const [buyingDeck, setBuyingDeck] = useState(false);
  const [showBack, setShowBack] = useState(false);

  async function notifyMe(e) {
    e.preventDefault();
    const value = waitlistEmail.trim();
    if (!value) return;
    setWaitlistStatus('loading');
    try {
      const res = await fetch(`${API}/api/email-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: 'Store Waitlist' }),
      });
      if (!res.ok) throw new Error();
      setWaitlistStatus('success');
      setWaitlistEmail('');
    } catch {
      setWaitlistStatus('error');
    }
  }

  async function buyEbook() {
    setBuying(true);
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: 'ebook-main-ingredient',
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setBuying(false);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      setBuying(false);
    }
  }

  async function buyDeck() {
    setBuyingDeck(true);
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: 'digital-card-deck',
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setBuyingDeck(false);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      setBuyingDeck(false);
    }
  }

  return (
    <section className="merch-page merch-page--soon" data-testid="merch-page">
      <div className="merch-hero">
        <h1 data-testid="merch-title" className="merch-title">The Village Store</h1>
        <p className="merch-subtitle">Wear your becoming. Carry your truth.</p>
      </div>

      {/* ════════════════════════════════════════════
          AVAILABLE NOW — Digital products
          ════════════════════════════════════════════ */}
      <div className="store-section" data-testid="store-available-now">
        <div className="store-section-header">
          <p className="store-eyebrow">Available Now · Instant Download</p>
          <h2 className="store-section-title">Digital Doorways</h2>
        </div>

        <div className="ebook-card" data-testid="ebook-card">
          <div className="ebook-card-cover">
            <img
              src={showBack ? EBOOK_BACK_COVER : EBOOK_COVER}
              alt={showBack ? 'Back cover — The Main Ingredient' : 'The Main Ingredient: F.U.C.K. T.H.A.T.T. Workbook'}
            />
            <div className="ebook-card-badge">PDF · Instant</div>
            <button
              type="button"
              className="ebook-card-flip"
              onClick={() => setShowBack((v) => !v)}
              data-testid="ebook-flip-btn"
              aria-label={showBack ? 'Show front cover' : 'Show back cover'}
            >
              {showBack ? 'Front' : 'Flip ↻'}
            </button>
          </div>

          <div className="ebook-card-body">
            <p className="ebook-card-eyebrow">eBook Workbook</p>
            <h3 className="ebook-card-title">
              The Main Ingredient<br/>
              <span className="ebook-card-title-accent">F.U.C.K. T.H.A.T.T.</span>
            </h3>
            <p className="ebook-card-sub">
              50 funny, spiritual prompts &amp; journal pages to stop playing
              small and cook up the LIFE you actually WANT.
            </p>

            <ul className="ebook-card-bullets">
              <li><Check size={14} strokeWidth={2.4} /> 50 bold, disruptive prompts</li>
              <li><Check size={14} strokeWidth={2.4} /> Cabrini’s Law of F.U.C.K. T.H.A.T.T.</li>
              <li><Check size={14} strokeWidth={2.4} /> Declarations that hit the core</li>
              <li><Check size={14} strokeWidth={2.4} /> 5 Life-Altering Principles</li>
              <li><Check size={14} strokeWidth={2.4} /><strong>PDF + EPUB</strong> · read on phone, Kindle, iPad · print at home</li>
            </ul>

            <div className="ebook-card-buy">
              <div className="ebook-card-price">
                <span className="ebook-card-price-amt">$9.99</span>
                <span className="ebook-card-price-tag">USD · one-time</span>
              </div>
              <button
                type="button"
                className="village-btn village-btn--primary ebook-card-btn"
                onClick={buyEbook}
                disabled={buying}
                data-testid="ebook-buy-btn"
              >
                {buying ? (
                  'Opening checkout…'
                ) : (
                  <>
                    <Download size={16} strokeWidth={2.2} />
                    Buy &amp; Download
                  </>
                )}
              </button>
            </div>
            <p className="ebook-card-fineprint">
              Secure checkout by Stripe. Both files delivered instantly after payment.
            </p>

            {/* Audio bundle teaser */}
            <div className="ebook-bundle-coming" data-testid="audio-bundle-coming">
              <span className="ebook-bundle-badge">Coming Soon</span>
              <div>
                <strong>The Audio Bundle</strong> · narrated by Cabrini herself.
                Listen in your car, your kitchen, your fire circle. Releases this summer.
              </div>
            </div>
          </div>
        </div>

        {/* ── Digital Card Deck (instant download) ── */}
        <div className="ebook-card" data-testid="deck-digital-card" style={{ marginTop: '1.5rem' }}>
          <div className="ebook-card-cover">
            <img src="/cards/box_front.webp" alt="F.U.C.K. T.H.A.T.T. Digital Card Deck — 50 cards" />
            <div className="ebook-card-badge">PDF · Instant</div>
          </div>

          <div className="ebook-card-body">
            <p className="ebook-card-eyebrow">Digital Deck</p>
            <h3 className="ebook-card-title">
              The F.U.C.K. T.H.A.T.T.<br />
              <span className="ebook-card-title-accent">Card Deck · Digital</span>
            </h3>
            <p className="ebook-card-sub">
              All 50 tarot-style cards as a print-ready PDF — pull one each morning
              on your phone, or print them at home for your own ritual deck.
            </p>

            <ul className="ebook-card-bullets">
              <li><Check size={14} strokeWidth={2.4} /> All 50 cards — fronts &amp; Cabrini Check-Ins</li>
              <li><Check size={14} strokeWidth={2.4} /> High-resolution, print-at-home ready</li>
              <li><Check size={14} strokeWidth={2.4} /> Instant download · keep forever</li>
              <li><Check size={14} strokeWidth={2.4} /> Pairs with the free daily <strong>Pull a Card</strong></li>
            </ul>

            <div className="ebook-card-buy">
              <div className="ebook-card-price">
                <span className="ebook-card-price-amt">$14.99</span>
                <span className="ebook-card-price-tag">USD · one-time</span>
              </div>
              <button
                type="button"
                className="village-btn village-btn--primary ebook-card-btn"
                onClick={buyDeck}
                disabled={buyingDeck}
                data-testid="deck-buy-btn"
              >
                {buyingDeck ? (
                  'Opening checkout…'
                ) : (
                  <>
                    <Download size={16} strokeWidth={2.2} />
                    Buy &amp; Download
                  </>
                )}
              </button>
              <GiftModal
                productId="digital-card-deck"
                productLabel="the Digital Deck"
                buttonLabel="Gift it"
                buttonClassName="village-btn village-btn--secondary ebook-card-btn"
                testId="deck-gift-btn"
              />
            </div>
            <p className="ebook-card-fineprint">
              Secure checkout by Stripe. PDF delivered instantly after payment.
            </p>
          </div>
        </div>

        <div className="ebook-also">
          <Sparkles size={14} aria-hidden="true" />
          <span>
            <strong>Brand new release</strong> — available here first, before
            Amazon. Be among the first to read it.
          </span>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          FEATURED — F.U.C.K. T.H.A.T.T. Card Deck (Coming Soon)
          ════════════════════════════════════════════ */}
      <div className="store-section" data-testid="store-card-deck">
        <div className="store-section-header">
          <p className="store-eyebrow">New · Physical Deck</p>
          <h2 className="store-section-title">The F.U.C.K. T.H.A.T.T. Card Deck</h2>
        </div>

        <div className="deck-feature" data-testid="card-deck-feature">
          <div className="deck-feature-fan" aria-hidden="true">
            {DECK_CARDS.map((src, i) => (
              <img key={src} src={src} alt="" className={`deck-feature-card deck-feature-card--${i}`} loading="lazy" />
            ))}
          </div>
          <div className="deck-feature-body">
            <span className="deck-feature-badge">Coming Soon · $29.99</span>
            <p className="deck-feature-sub">
              50 gold-foiled inspiration cards — each with a bold truth on the
              front and a <strong>Cabrini Check-In</strong> on the back. Pull one
              every morning, or pass them around your Fire Circle.
            </p>
            <p className="deck-feature-sub" style={{ color: '#d4af37', fontSize: '0.88rem' }}>
              🎁 Already own the <strong>Digital Deck</strong>? You&apos;ve earned <strong>$15 off</strong> —
              we&apos;ll email your code the moment the boxed deck launches.
            </p>
            <ul className="ebook-card-bullets">
              <li><Check size={14} strokeWidth={2.4} /> All 50 tarot-style cards, boxed</li>
              <li><Check size={14} strokeWidth={2.4} /> Tree of Life + hummingbird artwork</li>
              <li><Check size={14} strokeWidth={2.4} /> A daily ritual you can hold</li>
            </ul>
            <div className="deck-feature-actions">
              <Link to="/pull-a-card" className="village-btn village-btn--secondary" data-testid="card-deck-try-btn">
                Try a Free Pull
              </Link>
              <button
                type="button"
                className="village-btn village-btn--primary"
                data-testid="card-deck-waitlist-btn"
                onClick={() => {
                  const el = document.getElementById('store-coming-soon');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                Join the Waitlist
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          COMING SOON — Physical merch
          ════════════════════════════════════════════ */}
      <div className="store-section" id="store-coming-soon" data-testid="store-coming-soon">
        <div className="store-section-header">
          <p className="store-eyebrow">Merch · Crafting the Collection</p>
          <h2 className="store-section-title">Coming Soon to The Village</h2>
        </div>

        <div className="merch-soon-card">
          <div className="merch-soon-glow" aria-hidden="true" />
          <Sparkles size={22} className="merch-soon-icon" />
          <p className="merch-soon-sub">
            Tees, hoodies, journals &amp; mugs designed with sacred intention
            are being readied with our print partners. Join the waitlist — be
            first when doors open. 🌙
          </p>

          {waitlistStatus === 'success' ? (
            <div className="merch-soon-success" data-testid="merch-waitlist-success">
              <Check size={16} strokeWidth={2.4} />
              <span>You’re on the list — we’ll whisper when doors open.</span>
            </div>
          ) : (
            <form onSubmit={notifyMe} className="merch-soon-form" data-testid="merch-waitlist-form">
              <input
                type="email"
                required
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email for store waitlist"
                data-testid="merch-waitlist-input"
                disabled={waitlistStatus === 'loading'}
              />
              <button
                type="submit"
                className="village-btn village-btn--primary"
                data-testid="merch-waitlist-submit"
                disabled={waitlistStatus === 'loading' || !waitlistEmail.trim()}
              >
                {waitlistStatus === 'loading' ? 'Joining…' : 'Notify Me'}
              </button>
            </form>
          )}
          {waitlistStatus === 'error' && (
            <p className="merch-soon-error">Something went wrong. Try again, sister.</p>
          )}
        </div>

        <div className="merch-soon-grid" data-testid="merch-soon-grid">
          {PREVIEW_PRODUCTS.map((p) => (
            <figure key={p.name} className="merch-soon-tile">
              <img src={p.image} alt={p.name} loading="lazy" />
              <figcaption>{p.name}</figcaption>
              <div className="merch-soon-watermark">Coming Soon</div>
            </figure>
          ))}
        </div>
      </div>

      <div className="merch-cta">
        <Link to="/life-map" className="village-btn village-btn--secondary">
          Back to Life-Map
        </Link>
      </div>
    </section>
  );
}
